import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useRequests } from '../../../../requests/useRequests'
import { RefreshActions } from '../../../../state/refresh'
import { Box } from '@mui/material'
import { CollaboratorsModal } from './CollaboratorsModal'

interface TeamSectionProps {
  projectId: string
}

export const TeamSection = ({ projectId }: TeamSectionProps) => {
  const dispatch = useDispatch()
  const requestMaker = useRequests()

  const [projectCollaborators, setProjectCollaborators] = useState<Array<{
    id: string
    email: string
    role: string
    status: string
    createdAt: string
    firstName?: string
    lastName?: string
  }>>([])
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false)
  const [newCollaboratorEmail, setNewCollaboratorEmail] = useState('')
  const [newRole, setNewRole] = useState('')
  const [newFirstName, setNewFirstName] = useState('')
  const [newLastName, setNewLastName] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isAddingCollaborator, setIsAddingCollaborator] = useState(false)

  // Funkcja pobierająca członków zespołu
  const fetchProjectCollaborators = async () => {
    setIsLoading(true)
    try {
      const response = await requestMaker.getProjectCollaborators(projectId)
      setProjectCollaborators(response.data)
    } catch (error) {
      console.error('Błąd podczas pobierania członków zespołu', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się pobrać członków zespołu'
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  // Pobierz członków zespołu przy pierwszym renderowaniu
  useEffect(() => {
    fetchProjectCollaborators()
  }, [projectId])

  // Funkcja dodająca nowego członka zespołu
  const addCollaborator = async () => {
    if (!newCollaboratorEmail.trim() || !newRole) return

    try {
      setIsAddingCollaborator(true)
      setShowCollaboratorsModal(false)
      await requestMaker.inviteProjectCollaborator(projectId, {
        email: newCollaboratorEmail,
        role: newRole,
        firstName: newFirstName,
        lastName: newLastName
      })
      setNewCollaboratorEmail('')
      setNewRole('')
      setNewFirstName('')
      setNewLastName('')
      await fetchProjectCollaborators()
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Zaproszenie zostało wysłane'
        })
      )
    } catch (error) {
      console.error('Błąd podczas dodawania członka zespołu', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się dodać członka zespołu'
        })
      )
    } finally {
      setIsAddingCollaborator(false)
    }
  }

  // Funkcja usuwająca członka zespołu
  const removeCollaborator = async (collaboratorId: string) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tego członka zespołu?')) return

    try {
      await requestMaker.removeProjectCollaborator(projectId, collaboratorId)
      await fetchProjectCollaborators()
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Członek zespołu został usunięty'
        })
      )
    } catch (error) {
      console.error('Błąd podczas usuwania członka zespołu', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się usunąć członka zespołu'
        })
      )
    }
  }

  // Funkcja formatująca datę
  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return ''
      return date.toLocaleDateString('pl-PL')
    } catch (error) {
      console.error('Błąd formatowania daty:', error)
      return ''
    }
  }

  return (
    <Box>
      <div className='row mb-30'>
        <div className='col-sm-12'>
          <div className='card'>
            <div className='projectCard'>
              <div className='d-flex justify-content-between align-items-center mb-4'>
                <h3 className='mb-0 tw-font-semibold'>Zespół projektu</h3>
                <button
                  className={`
                      tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                      tw-text-white
                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                      ${isAddingCollaborator ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}
                    `}
                  onClick={() => setShowCollaboratorsModal(true)}
                  disabled={isAddingCollaborator}
                >
                  {isAddingCollaborator
                    ? (
                      <>
                        <span className='spinner-border spinner-border-sm mr-2' role='status' aria-hidden='true' />
                        Dodawanie...
                      </>
                      )
                    : (
                      <>
                        <i className='fa fa-plus mr-2' /> Dodaj członka zespołu
                      </>
                      )}
                </button>
              </div>

              {isLoading || isAddingCollaborator
                ? (
                  <div className='text-center py-50'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='sr-only'>Ładowanie...</span>
                    </div>
                    <p className='mt-20'>{isAddingCollaborator ? 'Dodawanie nowego członka zespołu...' : 'Ładowanie członków zespołu...'}</p>
                  </div>
                  )
                : (
                  <>
                    {projectCollaborators.length === 0
                      ? (
                        <div className='alert alert-info'>
                          Ten projekt nie ma jeszcze żadnych członków zespołu.
                        </div>
                        )
                      : (
                        <div className='table-responsive'>
                          <table className='table'>
                            <thead>
                              <tr>
                                <th>Email</th>
                                <th>Imię i nazwisko</th>
                                <th>Rola</th>
                                <th>Data dodania</th>
                                <th>Akcje</th>
                              </tr>
                            </thead>
                            <tbody>
                              {projectCollaborators.map((collaborator) => (
                                <tr key={collaborator.id}>
                  <td>{collaborator.email}</td>
                  <td>
                                  {collaborator.firstName || collaborator.lastName
                                    ? `${collaborator.firstName || ''} ${collaborator.lastName || ''}`.trim()
                                    : '-'}
                                </td>
                  <td>
                                  <span className='badge badge-info'>
                                    {collaborator.role}
                                  </span>
                                </td>
                  <td>{formatDate(collaborator.createdAt)}</td>
                  <td>
                                  <button
                                    className='tw-text-red-500 hover:tw-text-red-700 tw-p-2 tw-rounded-md focus:tw-outline-none'
                                    onClick={async () => await removeCollaborator(collaborator.id)}
                                    title='Usuń członka zespołu'
                                  >
                                    <i className='fa fa-trash tw-text-lg' />
                                  </button>
                                </td>
                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        )}
                  </>
                  )}
            </div>
          </div>
        </div>
      </div>

      <CollaboratorsModal
        show={showCollaboratorsModal}
        onClose={() => setShowCollaboratorsModal(false)}
        newCollaboratorEmail={newCollaboratorEmail}
        setNewCollaboratorEmail={setNewCollaboratorEmail}
        newRole={newRole}
        setNewRole={setNewRole}
        newFirstName={newFirstName}
        setNewFirstName={setNewFirstName}
        newLastName={newLastName}
        setNewLastName={setNewLastName}
        projectCollaboratorsLength={projectCollaborators.length}
        onAddCollaborator={addCollaborator}
      />
    </Box>
  )
}

export default TeamSection
